/**
 * The signing secret, shown once.
 *
 * Two acts mint one: creating an endpoint with signing, and rotating. Both
 * return the plaintext in the response body and never again, so both end
 * here. Modelled on PartnerTokenDialog for the same reason it exists — a
 * reveal-once value dismissed by reflex is gone.
 */

import { Button } from '../../ui/Button.js';
import { Typography } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { Dialog } from '../../ui/Dialog.js';

export function WebhookSecretDialog({
  secret,
  onClose,
}: {
  secret: string | null;
  onClose: () => void;
}) {
  const toast = useToast();
  return (
    <Dialog
      open={secret !== null}
      onClose={onClose}
      icon="key"
      tone="warn"
      title="Webhook signing secret"
      subtitle="Shown once. Give it to the receiving side before closing."
    >
      <p style={{ marginBottom: 16, fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
        Deliveries are signed with this secret. The receiver verifies
        <code
          style={{ marginLeft: 5, marginRight: 5, fontSize: 'var(--text-meta-lg)' }}
          className="mono"
        >
          HMAC-SHA256(secret, &quot;&#123;timestamp&#125;.&#123;body&#125;&quot;)
        </code>
        against the{' '}
        <code style={{ fontSize: 'var(--text-meta-lg)' }} className="mono">
          X-Optum-Signature
        </code>{' '}
        header.
      </p>
      <div
        style={{
          marginBottom: 18,
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--warn)',
          borderStyle: 'dashed',
          background: 'var(--warn-tint)',
          padding: 11,
        }}
      >
        <Typography variant="eyebrow" style={{ marginBottom: 7, display: 'block' }}>
          Signing secret
        </Typography>
        <code
          style={{
            display: 'block',
            wordBreak: 'break-all',
            fontSize: 'var(--text-meta-lg)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--warn)',
          }}
          className="mono"
        >
          {secret}
        </code>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(secret ?? '');
            toast.success('Copied to clipboard');
          }}
        >
          Copy
        </Button>
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      </div>
    </Dialog>
  );
}
